import { useState } from 'react';
import { ListenerRole } from '../../hooks/usePeers';
import Modal from './modal';

interface PeerListenerSelectorModalProps {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  selectedListenerIds: number[];
  setSelectedListenerIds: (ids: number[]) => void;
  listeners: ListenerRole[] | undefined;
  listenerOrPeer?: 'Listener' | 'Peer';
}

const PeerListenerSelectorModal: React.FC<PeerListenerSelectorModalProps> = ({
  showModal,
  setShowModal,
  selectedListenerIds,
  setSelectedListenerIds,
  listeners,
  listenerOrPeer = 'Listener',
}) => {
  const [listenerSearch, setListenerSearch] = useState('');
  const lowerCaseListenerOrPeer = listenerOrPeer.toLowerCase();

  const filteredListeners = listeners?.filter((listener) => {
    const searchLower = listenerSearch.toLowerCase();
    const displayNameLower = listener.user.display_name.toLowerCase();
    const idString = listener.id.toString();

    return displayNameLower.includes(searchLower) || idString.includes(searchLower);
  });

  const handleListenerToggle = (listenerId: number) => {
    const newSelectedIds = selectedListenerIds.includes(listenerId)
      ? selectedListenerIds.filter((id) => id !== listenerId)
      : [...selectedListenerIds, listenerId];
    setSelectedListenerIds(newSelectedIds);
  };

  return (
    <Modal
      isModel={showModal}
      InnerComponent={
        <div className="inline-block transform overflow-hidden bg-white p-7 align-bottom transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:align-middle">
          <div className="sm:flex sm:items-start">
            <div className="mt-3 w-full text-center sm:mt-0 sm:text-left">
              <div className="mb-4 flex items-center justify-between">
                <p className="text-lg font-medium leading-6 text-gray-900">{`Select ${listenerOrPeer}s`}</p>
              </div>
              <p className="mb-4 text-sm text-gray-500">
                Select the {lowerCaseListenerOrPeer} you want to see. You can search by name or Listener Role ID, and
                you can select multiple {lowerCaseListenerOrPeer}s.
              </p>
              <div>
                <p className="mb-2 text-sm font-medium text-gray-900">Search Peers</p>
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder={`Search ${listenerOrPeer}...`}
                    value={listenerSearch}
                    onChange={(e) => setListenerSearch(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:ring-transparent"
                    data-testid="search-listeners"
                  />
                </div>
                <div className="grid max-h-60 grid-cols-2 gap-4 overflow-y-auto">
                  {filteredListeners?.map((listener) => (
                    <button
                      key={listener.id}
                      type="button"
                      onClick={() => handleListenerToggle(listener.id)}
                      className={`rounded-lg p-4 text-left ${
                        selectedListenerIds.includes(listener.id)
                          ? 'border-blue-500 bg-blue-100'
                          : 'border-gray-200 bg-gray-50'
                      } border`}
                      data-testid={`listener-${listener.id}`}
                    >
                      {listener.user.display_name}
                      <span className="text-sm text-gray-500">{` (#${listener.id})`}</span>
                    </button>
                  ))}
                  {filteredListeners?.length === 0 && (
                    <div className="col-span-2 py-4 text-center text-gray-500">
                      No {lowerCaseListenerOrPeer}s found matching "{listenerSearch}"
                    </div>
                  )}
                </div>

                <p className="mt-4 text-sm text-gray-500">
                  {selectedListenerIds.length === 0
                    ? `No ${lowerCaseListenerOrPeer}s selected`
                    : `Selected ${selectedListenerIds.length} ${lowerCaseListenerOrPeer}(s)`}
                </p>
              </div>

              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="bg-blue-primary inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm sm:ml-3 sm:w-auto sm:text-sm"
                  data-testid="listener-selector-save-button"
                >
                  Done
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedListenerIds([])}
                  className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 sm:mt-0 sm:w-auto sm:text-sm"
                  data-testid="listener-selector-clear-button"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
};

export default PeerListenerSelectorModal;
